import { WSLoader } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { queriesExternalBankAccounts } from "@query";
import { selectorExternalBankAccountFundingReady } from "@selectors/selectorExternalBankAccountFundingReady";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_USERS_CLIENT } from "../../../../query/users/keys";
import { useClientQuery } from "../../../../query/users/queries";
import { usersService } from "../../../../services/users";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { ManageMethods } from "../ManageMethods";
import { MethodValue } from "../ManageMethods/types";

type Props = {
  onNext: () => void;
  onAddNew: () => void;
};

export const ManagePaymentMethod: React.FC<Props> = props => {
  const userId = useUserId();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryClient = useClientQuery(userId);

  const externalBankAccounts = useMemo(
    () =>
      (queryExternalBankAccounts.data?.data || []).filter(
        selectorExternalBankAccountFundingReady
      ),
    [queryExternalBankAccounts.data]
  );

  const creditCards = useMemo(
    () => queryClient.data?.profile.savedPaymentMethods,
    [queryClient.data?.profile.savedPaymentMethods]
  );

  const initialValue = useMemo((): MethodValue | undefined => {
    if (queryClient.data?.profile.defaultPaymentMethod?.accountId) {
      return {
        id: queryClient.data.profile.defaultPaymentMethod.accountId,
        type: "Account"
      };
    }

    if (queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId) {
      return {
        id: queryClient.data.profile.defaultPaymentMethod.paymentMethodId,
        type: "CreditCard"
      };
    }
  }, [
    queryClient.data?.profile.defaultPaymentMethod?.accountId,
    queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId
  ]);

  const [mutate, meta] = useWSMutation<
    void,
    WSServiceError,
    MethodValue | null
  >(
    async data => {
      if (!data) {
        return;
      }

      if (!["Account", "CreditCard"].includes(data.type)) {
        throw new Error("Not supported");
      }

      await usersService.client.update(userId, {
        clientId: userId,
        profile: {
          defaultPaymentMethod:
            data.type === "Account"
              ? {
                  accountId: data.id,
                  paymentMethodId: null
                }
              : {
                  accountId: null,
                  paymentMethodId: data.id
                }
        }
      });
    },
    { onSuccess: props.onNext, dependencies: [QUERY_USERS_CLIENT] }
  );

  if (queryExternalBankAccounts.isLoading || queryClient.isLoading) {
    return <WSLoader.Spinner />;
  }

  return (
    <ManageMethods
      data={{
        accounts: externalBankAccounts,
        creditCards
      }}
      header={{
        label: {
          text: "Manage payment method",
          helperText: "Saved methods, including default for autopay"
        }
      }}
      onAddNew={props.onAddNew}
      addNewLabel="Add new payment method"
      initialValue={initialValue}
      onSubmit={mutate}
      error={meta.error}
      isLoading={meta.isLoading}
    />
  );
};
